import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Upload, Car, Bike, ShieldCheck, AlertCircle, Check } from "lucide-react";
import { driverSignup, fileToDataUri } from "../../lib/ride";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const DOC_FIELDS = [
  { key: "cni_front", label: "CNI – recto", required: true },
  { key: "cni_back", label: "CNI – verso", required: false },
  { key: "license_front", label: "Permis – recto", required: true },
  { key: "license_back", label: "Permis – verso", required: false },
  { key: "vehicle_photo", label: "Photo du véhicule", required: false },
];

export default function DriverSignupPage() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: "",
    vehicle_type: "taxi",
    vehicle_plate: "",
    vehicle_color: "",
    vehicle_model: "",
    license_no: "",
    cni_no: "",
    cni_front: "",
    cni_back: "",
    license_front: "",
    license_back: "",
    vehicle_photo: "",
  });
  const [filePreview, setFilePreview] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!user || user === false) {
    return (
      <div className="max-w-md mx-auto px-5 py-12 text-center" data-testid="driver-signup-need-auth">
        <p className="text-brand-ink mb-3">Connectez-vous pour devenir chauffeur Kanama Ride.</p>
        <Link to="/login?next=/ride/driver/signup">
          <Button className="rounded-full bg-brand-gold hover:bg-brand-goldDark text-brand-black">Se connecter</Button>
        </Link>
      </div>
    );
  }

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUri = await fileToDataUri(file);
      setForm((prev) => ({ ...prev, [k]: dataUri }));
      setFilePreview((prev) => ({ ...prev, [k]: dataUri }));
    } catch (err) {
      toast.error(err.message || "Fichier invalide");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.cni_front || !form.license_front) {
      setError("La CNI recto et le permis recto sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await driverSignup(form);
      toast.success("Candidature envoyée — en attente de validation par l'admin");
      await refresh();
      navigate("/ride/driver");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-8" data-testid="driver-signup">
      <Link to="/ride" className="text-xs text-brand-inkSoft hover:text-brand-ink">← Retour Kanama Ride</Link>
      <h1 className="font-heading text-3xl font-semibold text-brand-ink mt-3 mb-2">Devenir chauffeur</h1>
      <p className="text-sm text-brand-inkSoft mb-6">
        Inscription en quelques minutes. Vos documents seront vérifiés par l'équipe Kanama avant validation.
      </p>

      <form onSubmit={submit} className="space-y-5">
        {/* Vehicle type */}
        <Card className="rounded-2xl border-brand-line p-4">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold">Type de véhicule</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { k: "taxi", label: "Taxi", Icon: Car, desc: "Voiture 4 places" },
              { k: "moto", label: "Moto-taxi", Icon: Bike, desc: "2 roues" },
            ].map(({ k, label, Icon, desc }) => {
              const active = form.vehicle_type === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setForm({ ...form, vehicle_type: k })}
                  data-testid={`driver-vehicle-${k}`}
                  className={`p-3 rounded-xl border-2 text-left transition-all min-h-[60px] ${
                    active ? "border-brand-gold bg-brand-gold/10" : "border-brand-line"
                  }`}
                >
                  <Icon className="w-5 h-5 mb-1 text-brand-gold" />
                  <div className="font-semibold text-sm text-brand-ink">{label}</div>
                  <div className="text-[11px] text-brand-inkMuted">{desc}</div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Identity */}
        <Card className="rounded-2xl border-brand-line p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold mb-1">Identité & véhicule</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Nom complet *</Label><Input required value={form.name} onChange={upd("name")} className="mt-1 h-12 rounded-xl text-base" autoComplete="name" data-testid="driver-input-name" /></div>
            <div><Label>Téléphone *</Label><Input required type="tel" inputMode="tel" value={form.phone} onChange={upd("phone")} placeholder="+235 ..." className="mt-1 h-12 rounded-xl text-base" autoComplete="tel" data-testid="driver-input-phone" /></div>
            <div><Label>N° CNI *</Label><Input required value={form.cni_no} onChange={upd("cni_no")} className="mt-1 h-12 rounded-xl text-base" data-testid="driver-input-cni" /></div>
            <div><Label>N° permis *</Label><Input required value={form.license_no} onChange={upd("license_no")} className="mt-1 h-12 rounded-xl text-base" data-testid="driver-input-license" /></div>
            <div><Label>Plaque d'immatriculation *</Label><Input required value={form.vehicle_plate} onChange={upd("vehicle_plate")} placeholder="AB 1234 TD" className="mt-1 h-12 rounded-xl text-base" data-testid="driver-input-plate" /></div>
            <div><Label>Couleur</Label><Input value={form.vehicle_color} onChange={upd("vehicle_color")} placeholder="Blanc" className="mt-1 h-12 rounded-xl text-base" data-testid="driver-input-color" /></div>
            <div className="sm:col-span-2"><Label>Marque / modèle</Label><Input value={form.vehicle_model} onChange={upd("vehicle_model")} placeholder="Toyota Corolla" className="mt-1 h-12 rounded-xl text-base" data-testid="driver-input-model" /></div>
          </div>
        </Card>

        {/* Documents */}
        <Card className="rounded-2xl border-brand-line p-4 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-brand-inkMuted font-bold">Documents</div>
          {DOC_FIELDS.map(({ key, label, required }) => (
            <div key={key} className="flex items-center gap-3">
              <label className="flex-1 cursor-pointer flex items-center gap-3 p-3 rounded-xl border-2 border-dashed border-brand-line hover:border-brand-gold transition-colors" data-testid={`driver-doc-${key}`}>
                {filePreview[key] ? (
                  <img src={filePreview[key]} alt={label} className="w-12 h-12 object-cover rounded-lg" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-brand-sandlight flex items-center justify-center text-brand-inkMuted shrink-0">
                    <Upload className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1">
                  <div className="text-sm font-semibold text-brand-ink">
                    {label} {required && <span className="text-brand-terracotta">*</span>}
                  </div>
                  <div className="text-[11px] text-brand-inkMuted">
                    {filePreview[key] ? "✓ Fichier importé" : "JPG / PNG · max 5 MB"}
                  </div>
                </div>
                <input type="file" accept="image/*" onChange={handleFile(key)} className="hidden" />
                {filePreview[key] && <Check className="w-5 h-5 text-emerald-600" />}
              </label>
            </div>
          ))}
        </Card>

        <div className="rounded-xl bg-brand-sandlight border border-brand-line p-3 flex items-start gap-2.5 text-xs text-brand-inkSoft">
          <ShieldCheck className="w-4 h-4 text-brand-gold shrink-0 mt-0.5" />
          <span>Vos documents sont chiffrés et utilisés uniquement pour la vérification. La validation prend généralement moins de 48h.</span>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2" data-testid="driver-signup-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button type="submit" disabled={submitting} className="w-full h-14 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white font-semibold text-base shadow-lg" data-testid="driver-signup-submit">
          {submitting ? "Envoi…" : "Envoyer ma candidature"}
        </Button>
      </form>
    </div>
  );
}
